import React, { useState } from "react";
import { Button } from "../../../components/ui/button";
import { AlertCircle, AlertTriangle, FileSpreadsheet, FileText, Info, Mail, MoreHorizontal, RotateCcw, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { CSVLink } from "react-csv";
import moment from "moment";
import styles from "../Report.module.scss";
import { Card } from "../../../components/ui/card";
import { Progress } from "../../../components/ui/progrss";
import { Dialog, DialogContent } from "../../../components/ui/dialog";
import { Badge } from "../../../components/ui/badge";

const CommonDownloadBar = ({
  group,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  reportName,
  startDate,
  endDate,
  setDownloadCSVData,
  client
}) => (
  <div className="flex flex-center flex-space-between sm:flex-row sm:justify-between sm:items-center bg-gray-50 dark:bg-gray-800 p-12 rounded-lg gap-2">
    <div>
    <h3 className="font-medium text-gray-900 dark:text-white text-sm">
      {group?.clientName}
    </h3>
    {client === true && (
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Last Service Date: {group?.records?.[0]?.dates}
      </div>
    )}
    </div>
    <div className="flex items-center space-x-2 self-start sm:self-center">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 hover:bg-green-50"
        onClick={() => handleEmailIconClick(group)}
      >
        <Mail className="h-3 w-3 text-green-600" />
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8">
            <MoreHorizontal className="mr-4 h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-40">
          <div className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start h-8"
              onClick={() => downloadForPDF(group)}
            >
              <FileText className="mr-8 h-3 w-3" />
              PDF
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-8"
              onClick={() => downloadForExcel(group)}
            >
              <FileSpreadsheet className="mr-8 h-3 w-3" />
              Excel
            </Button>
            <CSVLink
              data={clientsListCSV}
              filename={`${reportName}${group?.clientName}_${moment(startDate).format("YYYY-MM-DD")}_${moment(endDate).format("YYYY-MM-DD")}.csv`}
              asyncOnClick={true}
              onClick={(event, done) =>
                setDownloadCSVData(event, done, group)
              }
              className="w-full flex justify-start items-center h-8 pv-8 ph-16 text-sm hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
            >
              <FileSpreadsheet className="mr-16 h-4 w-4" />
              <span className="font-medium">CSV</span>
            </CSVLink>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </div>
);

export function MobileUpcomingAppointmentReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Scheduled':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'ReScheduled':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  return MobileViewData?.map((group) => (
    <div key={group?.clientName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="ml-2 shadow-sm">
          <div className="p-12 space-y-3">
            <div className="flex justify-between items-center text-xs pb-8 border-b">
              <div className="font-medium text-xs text-gray-900 dark:text-white">
                {record?.date}
              </div>
              <div className={`font-medium rounded-full px-2.5 py-0.5 ${getStatusBadgeColor(record?.status)} `}>
                {record?.status}
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-xs">
                <div>
                  <div className="text-gray-500 mb-4">Team</div>
                  <div className="text-gray-900 font-medium dark:text-white">
                    {record?.team}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-4">Shift</div>
                  <div className="text-blue-600 dark:text-white font-medium">
                    {record?.shift}
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center text-xs">
                <div>
                  <div className="text-gray-500 mb-4">Time</div>
                  <div className="text-blue-600 dark:text-white font-medium">
                    {record?.time}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-4">endTime</div>
                  <div className="text-blue-600 dark:text-white text-xs">
                    {record?.endtime}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-4">Location</div>
                <div className="text-gray-900 dark:text-white font-medium">
                  {record?.location}
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}

export function MobileClientIssueTrendsReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "High":
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case "Medium":
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case "Low":
        return <Info className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return MobileViewData?.map((group) => (
    <div key={group?.clientName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="ml-2 shadow-sm">
          <div className="p-12 space-y-3">
            <div className="flex justify-between items-center text-xs pb-8 border-b">
              <div className="font-medium text-xs text-gray-900 dark:text-white">
                {record?.date}
              </div>
              <Badge variant="secondaryNew" className={"dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"}>{record?.issue} issue</Badge>
            </div>
            <div className="grid grid-cols-3 gap-3 py-2">
              <div className="text-center p-8 bg-destructive/10 rounded-md">
                <div className="flex items-center justify-center gap-1 mb-4">
                  {getPriorityIcon("High")}
                  <span className="font-semibold text-sm">{record?.high}</span>
                </div>
                <div className="text-xs text-muted-foreground">High</div>
              </div>
              <div className="text-center p-8 bg-warning/10 rounded-md">
                <div className="flex items-center justify-center gap-1 mb-4">
                  {getPriorityIcon("Medium")}
                  <span className="font-semibold text-sm">
                    {record?.medium}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">Medium</div>
              </div>
              <div className="text-center p-8 bg-muted rounded-md">
                <div className="flex items-center justify-center gap-1 mb-4">
                  {getPriorityIcon("Low")}
                  <span className="font-semibold text-sm">{record?.low}</span>
                </div>
                <div className="text-xs text-muted-foreground">Low</div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}

export function MobileRescheduleAndCancelReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const getChangeTypeIcon = (type) => {
    return type === 'Cancellation' ?
      <X className="w-4 h-4 text-red-500" /> :
      <RotateCcw className="w-4 h-4 text-blue-500" />;
  };

  const getChangeTypeBadge = (type) => {
    return type === 'Cancellation' ?
      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full dark:bg-red-900 dark:text-red-200">
        Cancellation
      </span> :
      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full dark:bg-blue-900 dark:text-blue-200">
        Reschedule
      </span>;
  };


  return MobileViewData?.map((group) => (
    <div key={group?.clientName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="ml-2 shadow-sm">
          <div className="p-12 space-y-3">
            <div className="flex justify-between items-center text-xs pb-8 border-b">
              <div className="font-medium text-xs text-gray-900 dark:text-white">
                {moment(record?.date).format("MM/DD/YYYY")} at {moment(record?.date).format("hh:mm A")}
              </div>
              <div className={`flex items-center space-x-2`}>
                {getChangeTypeIcon(record?.type)}
                {getChangeTypeBadge(record?.type)}
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-xs">
                <div>
                  <div className="text-gray-500 mb-4">Employee</div>
                  <div className="text-gray-900 font-medium dark:text-white">
                    {record?.employee}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-4">New Date</div>
                  <div className="text-blue-600 dark:text-white font-medium">
                    <div>{moment(record?.newDate).format("MM/DD/YYYY")}</div>
                    <div>{moment(record?.newDate).format("hh:mm A")}</div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between text-xs">
                <div>
                  <div className="text-gray-500 mb-4">Updated By</div>
                  <div className="text-blue-600 dark:text-white text-xs">
                    {record?.updatedBy}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-4">Updated</div>
                  <div className="text-blue-600 dark:text-white font-medium">
                    <div>{moment(record?.updated).format("MM/DD/YYYY")} </div>
                    <div>{moment(record?.updated).format("hh:mm A")} </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}

export function MobileRepeatVisitsNoShowsReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const getStatusBadgeColor = (type) => {
    switch (type) {
      case "Repeat":
        return "bg-blue-100 text-blue-600";
      case "No-Show":
        return "bg-red-100 text-red-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };
  const [dialog, setDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState();
  let employeeNames = [];


  return MobileViewData?.map((group) => (
    <>
      <div key={group?.clientName} className="space-y-3 mb-15">
        <CommonDownloadBar
          group={group}
          handleEmailIconClick={handleEmailIconClick}
          downloadForPDF={downloadForPDF}
          downloadForExcel={downloadForExcel}
          clientsListCSV={clientsListCSV}
          reportName={reportName}
          startDate={startDate}
          endDate={endDate}
          setDownloadCSVData={setDownloadCSVData}
        />

        {group?.records?.map((record) => (
          <Card key={record?.id} className="ml-2 shadow-sm">
            <div className="p-12 space-y-3">

              <div className="space-y-3">
                <div className="flex justify-between items-center text-xs border-b pb-10">
                  <div className="dark:text-white font-medium">
                    {record?.clientdate}
                  </div>
                  <div className={`font-medium rounded-full px-2.5 py-0.5 ${getStatusBadgeColor(record?.type)} `}>
                    {record?.type}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">

                  <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded" onClick={() => {
                    setSelectedRecord(record);
                    setDialog(true);
                  }}>
                    <p className="text-xs text-muted-foreground font-medium mb-5">
                      Employee
                    </p>
                    <div className="text-medium  font-medium text-blue-600">
                      {record?.employee}
                    </div>
                  </div>
                  <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                    <p className="text-xs text-muted-foreground font-medium mb-5">
                      Count
                    </p>
                    <div className="text-medium  font-medium">
                      {record?.count}
                    </div>
                  </div>

                </div>
                <div>
                  <div className="mb-4">Location</div>
                  <div className="dark:text-white text-xs">
                    {record?.clientaddress}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {dialog && (
        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogContent>
            <h2 className="text-lg font-bold mb-4">Employee List</h2>
            <ul className="space-y-2">
              {selectedRecord.employeeNames.map((name, index) => (
                <li
                  key={index}
                  className="p-8 border rounded-md hover:bg-gray-100 cursor-pointer"
                >
                  {name}
                </li>
              ))}
            </ul>
            <Button onClick={() => setDialog(false)}>Close</Button>
          </DialogContent>
        </Dialog>
      )}

    </>
  ));
}

export function MobileMostActiveClientReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  return MobileViewData?.map((group) => (
    <div key={group?.clientName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
        client={true}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="ml-2 shadow-sm">
          <div className="p-12 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium">
                  Appt. Count
                </p>
                <p className="text-lg font-bold text-primary">
                  {record?.count}
                </p>
              </div>
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium">
                  Tasks
                </p>
                <p className="text-lg font-bold">
                  {record?.totalTasks > 0 ? `${Math.round((record?.completedTasks / record?.totalTasks) * 100)}%` : "0%"}
                </p>
              </div>
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium">
                  Cancelled
                </p>
                <p className="text-lg font-bold text-destructive">
                  {record?.canceled}
                </p>
              </div>
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium">
                  Rescheduled
                </p>
                <p className="text-lg font-bold text-orange-600">
                  {record?.reschedule}
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground font-medium mb-4">
                Employees ({record?.employee?.length})
              </p>
              <div className="text-xs text-muted-foreground">
                {record?.employee?.join(", ")}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}

export function MobileTaskSummaryReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const getCompletionRateColor = (rate) => {
    if (rate >= 90) return 'text-green-600 dark:text-green-400';
    if (rate >= 80) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  return MobileViewData?.map((group) => (
    <div key={group?.clientName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="ml-2 shadow-sm">
          <div className="p-12 space-y-3">
            <div className="flex justify-between items-center text-xs pb-8 border-b">
              <div className="font-medium text-xs text-gray-900 dark:text-white">
                {moment(record?.startDate).format("MMM YYYY")} •{" "}
                {moment(record?.endDate).format("MMM YYYY")}
              </div>
              <div className="flex items-center space-x-2">
                <Progress value={record?.completionrate} className="w-12 h-2" />
                <span
                  className={`text-sm font-medium ${getCompletionRateColor(
                    record?.completionrate
                  )}`}
                >
                  {record?.completionrate}%
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium">
                  Total
                </p>
                <div className="text-xs text-muted-foreground font-medium">
                  {record?.completed}/{record?.assigned}
                </div>
              </div>
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium">
                  AVG Time
                </p>
                <p className="text-lg font-medium">
                  {record?.avgtime}
                </p>
              </div>
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium">
                  Pending
                </p>
                <p className="text-lg font-bold text-destructive">
                  {record?.pending}
                </p>
              </div>
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium">
                  OverDue
                </p>
                <p className="text-lg font-bold text-orange-600">
                  {record?.overdue}
                </p>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}